import { z } from 'zod'
import { oc } from '@orpc/contract'

/**
 * Zocdoc's own `cancellation_reason_type` vocabulary, verbatim
 * ([docs](https://api-docs.zocdoc.com/apis/appointments/cancelappointment)).
 *
 * Ordered the way Zocdoc groups it — the patient's reasons first, then the practice's — because
 * that is the order the operator's picker reads best in.
 *
 * Lives in the contract rather than beside the client: it is part of the wire shape, and a
 * contract may only import other contracts (the web bundle compiles this file).
 */
export const ZOCDOC_CANCELLATION_REASON_TYPES = [
  'patient_no_longer_needs_appointment',
  'patient_no_longer_available',
  'other_patient_reason',
  'missing_needed_patient_information',
  'payment_or_insurance_issue',
  'patient_or_visit_type_not_accepted',
  'provider_not_available',
  'rescheduling_patient',
  'other_provider_reason',
] as const

export type ZocdocCancellationReasonType = (typeof ZOCDOC_CANCELLATION_REASON_TYPES)[number]

/** The two types Zocdoc wants the free-text `cancellation_reason` alongside. */
export const ZOCDOC_REASON_TYPES_NEEDING_TEXT: readonly ZocdocCancellationReasonType[] = [
  'other_patient_reason',
  'other_provider_reason',
]

/**
 * Cancels a booking **on Zocdoc** (`POST /v1/appointments/cancel`).
 *
 * **One action, one route.** Zocdoc has no single "update appointment" endpoint — confirm, cancel,
 * reschedule and arrived/no-show are four separate writes with disjoint payloads and different
 * legal source states (docs/ZOCDOC_API_DATA_SOURCES.md §5). So each gets its own contract, whose
 * input is only what that action needs and whose refusals are only what that action can refuse.
 *
 * **It does not touch the beHuman calendar.** If the booking is bound to a beHuman appointment,
 * the response names it and `behumanStillBooked` is set, so the screen can say so out loud — but
 * cancelling here writes nothing to FHIR. Deliberate: the beHuman cancellation hard-deletes the
 * `Appointment` and `Encounter` behind a billing-ledger guard that can refuse with a 409
 * (`AppointmentService.cancelAppointment`), and a hard delete driven implicitly by a Zocdoc button
 * is not something to do without the operator asking for it. Mirroring the two sides in one click
 * is the next step, not this one.
 *
 * **Never retried.** Zocdoc offers no idempotency key, so an uncertain failure means "we do not
 * know" — the screen re-reads the row rather than sending a second cancellation.
 */
export const zocdocCancelAppointment = oc
  .route({ method: 'POST', path: '/v2/zocdoc/appointments/cancel' })
  .input(
    z.object({
      zocdocAppointmentId: z.string().min(1),
      /** Zocdoc's own vocabulary, passed through verbatim. */
      reasonType: z.enum(ZOCDOC_CANCELLATION_REASON_TYPES),
      /**
       * The free-text half, which Zocdoc wants only for the two `other_*` types. Required for
       * them and rejected for the rest — enforced in the handler, where the pairing rule lives.
       */
      reason: z.string().trim().min(1).max(500).optional(),
    }),
  )
  .output(
    z.object({
      status: z.enum([
        /** Zocdoc accepted the cancellation. */
        'cancelled',
        /** Zocdoc already had it cancelled — nothing was sent. */
        'already-cancelled',
        /** Nothing was sent. See `refusal`. */
        'refused',
      ]),
      refusal: z
        .object({
          reason: z.enum([
            /** Zocdoc does not know this appointment id. */
            'not-found',
            /**
             * The appointment's status is not one Zocdoc cancels from. Legal states are
             * `pending_booking`, `booking_failed`, `confirmed`, `pending_reschedule`,
             * `reschedule_failed` and `rescheduled`.
             */
            'status-not-cancellable',
            /** An `other_*` type arrived without its free text, or free text without one. */
            'reason-mismatch',
            /** Zocdoc rejected the write — most often the account lacks the cancel scope. */
            'zocdoc-rejected',
          ]),
          detail: z.string(),
        })
        .optional(),
      /** The status Zocdoc reported before the write, for a refusal the screen has to explain. */
      previousStatus: z.string().optional(),
      /**
       * What Zocdoc reported when the appointment was re-read **immediately after** the write.
       *
       * Exists because a successful write plus an unchanged row is otherwise silent: the screen
       * re-reads the list, shows the same status, and cannot say whether Zocdoc rejected the
       * change or has simply not caught up. Absent when the verifying read failed — which is not
       * itself a failure of the write.
       */
      observedStatus: z.string().optional(),
      /** The bound beHuman appointment, when there is one — untouched by this call. */
      behumanAppointmentId: z.string().optional(),
      behumanEncounterId: z.string().optional(),
      /**
       * Set when a beHuman appointment is still on the calendar for this booking. Not a failure:
       * the Zocdoc side is cancelled and somebody has to cancel ours too.
       */
      behumanStillBooked: z.boolean().optional(),
    }),
  )
